import { createContext, useContext, useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Valeurs par défaut
const DEFAULT_SETTINGS = {
  brand: {
    store_name: { fr: 'Mutagim.shop', he: 'מוטאגים.שופ', en: 'Mutagim.shop' },
    slogan: { fr: 'Streetwear Premium', he: 'סטריטוור פרימיום', en: 'Premium Streetwear' },
    logo_dark: null,
    logo_light: null,
    favicon: null
  },
  promo_banner: {
    enabled: true,
    text: {
      fr: 'Livraison gratuite dès 200₪ d\'achat',
      he: 'משלוח חינם בקניה מעל 200₪',
      en: 'Free shipping on orders over 200₪'
    },
    background_color: '#C0000C',
    text_color: '#FFFFFF',
    link_url: null,
    link_target: '_self'
  },
  header: {
    nav_links: [
      { id: 'home', label: { fr: 'Accueil', he: 'בית', en: 'Home' }, url: '/', order: 0, enabled: true },
      { id: 'products', label: { fr: 'Produits', he: 'מוצרים', en: 'Products' }, url: '/products', order: 1, enabled: true },
      { id: 'categories', label: { fr: 'Catégories', he: 'קטגוריות', en: 'Categories' }, url: '/categories', order: 2, enabled: true },
      { id: 'notre-systeme', label: { fr: 'Notre Système', he: 'המערכת שלנו', en: 'Our System' }, url: '/notre-systeme', order: 3, enabled: true }
    ],
    social_links: [],
    show_language_switch: true,
    show_currency_switch: true
  },
  slider: {
    slides: [],
    autoplay: true,
    autoplay_speed: 5000,
    show_dots: true,
    show_arrows: true
  },
  footer: {
    columns: [],
    copyright_text: {
      fr: '© 2026 Mutagim.shop. Tous droits réservés.',
      he: '© 2026 מוטאגים.שופ. כל הזכויות שמורות.',
      en: '© 2026 Mutagim.shop. All rights reserved.'
    },
    show_newsletter: true,
    newsletter_title: { fr: 'Restez informé', he: 'הישארו מעודכנים', en: 'Stay Updated' },
    newsletter_placeholder: { fr: 'Votre email', he: 'האימייל שלכם', en: 'Your email' }
  }
};

const StoreSettingsContext = createContext(null);

export function StoreSettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // TOUJOURS charger depuis le réseau en premier (Network First)
        const response = await fetch(`${API_URL}/api/settings/public`, {
          cache: 'no-store', // Désactiver le cache HTTP
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (!response.ok) throw new Error('Failed to fetch settings');
        
        const data = await response.json();
        setSettings(data);
        
        // Mettre en cache pour fallback offline uniquement
        localStorage.setItem('mutagim_store_settings', JSON.stringify(data));
        localStorage.setItem('mutagim_store_settings_time', Date.now().toString());
        
        setError(null);
      } catch (err) {
        console.error('Error fetching store settings:', err);
        
        // En cas d'erreur réseau, utiliser le cache localStorage comme fallback
        const cached = localStorage.getItem('mutagim_store_settings');
        if (cached) {
          try {
            const cachedData = JSON.parse(cached);
            setSettings(cachedData);
            console.log('Using cached store settings as fallback');
          } catch (parseErr) {
            // Cache corrompu, utiliser les défauts
            localStorage.removeItem('mutagim_store_settings');
            localStorage.removeItem('mutagim_store_settings_time');
            setSettings(DEFAULT_SETTINGS);
          }
        } else {
          setSettings(DEFAULT_SETTINGS);
        }
        
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  // Fonction pour forcer le rechargement des settings
  const refreshSettings = async () => {
    localStorage.removeItem('mutagim_store_settings');
    localStorage.removeItem('mutagim_store_settings_time');
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/settings/public`);
      if (!response.ok) throw new Error('Failed to fetch settings');
      
      const data = await response.json();
      setSettings(data);
      
      localStorage.setItem('mutagim_store_settings', JSON.stringify(data));
      localStorage.setItem('mutagim_store_settings_time', Date.now().toString());
    } catch (err) {
      console.error('Error refreshing store settings:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <StoreSettingsContext.Provider value={{ settings, loading, error, refreshSettings }}>
      {children}
    </StoreSettingsContext.Provider>
  );
}

export function useStoreSettings() {
  const context = useContext(StoreSettingsContext);
  if (!context) {
    throw new Error('useStoreSettings must be used within StoreSettingsProvider');
  }
  return context;
}

// Helper pour obtenir le texte dans la langue courante
export function getLocalizedText(obj, lang = 'fr') {
  if (!obj) return '';
  return obj[lang] || obj.fr || obj.en || obj.he || '';
}

export default StoreSettingsContext;
